import { Spin } from 'antd';
import { LatLng } from 'leaflet';
import React, { useState } from 'react';
import { Circle, MapContainer, TileLayer, useMap } from 'react-leaflet';

interface LocationRadiusMapProps {
  center: { lat: number; lng: number };
  radius: number; // in kilometers
}

// Component to handle map center updates
const MapCenterUpdater: React.FC<{ center: LatLng }> = ({ center }) => {
  const map = useMap();
  React.useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
};

// Component to handle zoom updates based on radius
const MapZoomUpdater: React.FC<{ zoom: number }> = ({ zoom }) => {
  const map = useMap();
  React.useEffect(() => {
    map.setZoom(zoom);
  }, [zoom, map]);
  return null;
};

export const LocationRadiusMap: React.FC<LocationRadiusMapProps> = ({ center, radius }) => {
  const [isLoading, setIsLoading] = useState(true);
  const position = new LatLng(center.lat, center.lng);
  const radiusInMeters = radius * 1000;

  // Calculate appropriate zoom level based on radius
  const getZoomLevel = (radiusKm: number): number => {
    if (radiusKm <= 2) return 12;
    if (radiusKm <= 5) return 11;
    if (radiusKm <= 10) return 10;
    if (radiusKm <= 25) return 9;
    if (radiusKm <= 50) return 8;
    if (radiusKm <= 100) return 7;
    return 6;
  };

  const zoom = getZoomLevel(radius);

  return (
    <div className="w-full h-64 rounded-lg overflow-hidden border border-gray-200 relative bg-gray-50">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-10 bg-white bg-opacity-90">
          <Spin size="large" />
        </div>
      )}
      <MapContainer
        center={position}
        zoom={zoom}
        scrollWheelZoom={false}
        style={{ height: '100%', width: '100%' }}
        whenCreated={() => setIsLoading(false)}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          eventHandlers={{
            load: () => setIsLoading(false),
          }}
        />
        <Circle
          center={position}
          radius={radiusInMeters}
          pathOptions={{
            fillColor: '#1890ff',
            fillOpacity: 0.1,
            color: '#1890ff',
            weight: 2,
          }}
        />
        <MapCenterUpdater center={position} />
        <MapZoomUpdater zoom={zoom} />
      </MapContainer>
    </div>
  );
};
